import type { CanvasDrawTool } from '../../stores/uiStore';
import { useUIStore } from '../../stores/uiStore';
import { NODE_TYPES } from '../../constants/nodeTypes';

interface DrawToolHintProps {
  tool: CanvasDrawTool;
  stampType?: string | null;
  onExit: () => void;
}

const HINTS: Record<Exclude<CanvasDrawTool, 'none'>, { text: string; color: string }> = {
  frame: {
    text: 'Annotation frame — drag to draw a visual board',
    color: '#f59e0b',
  },
  box: {
    text: 'Box — click to drop, or drag to size',
    color: '#818cf8',
  },
  arrow: {
    text: 'Annotation arrow — drag on the canvas',
    color: '#94a3b8',
  },
  line: {
    text: 'Line — drag on the canvas',
    color: '#94a3b8',
  },
  text: {
    text: 'Text — click the canvas to type a label',
    color: '#e2e8f0',
  },
};

export function FrameDrawHint({ tool, stampType, onExit }: DrawToolHintProps) {
  const sticky = useUIStore(s => s.canvasToolSticky);
  const connectMode = useUIStore(s => s.canvasConnectMode);
  const stampMeta = stampType ? NODE_TYPES[stampType as keyof typeof NODE_TYPES] : undefined;

  const mode = sticky ? 'sticky — keeps drawing' : 'once — click the tool again to keep drawing';
  const hint = connectMode !== 'none'
    ? {
        text: connectMode === 'sticky'
          ? 'Connect sticky — click nodes to chain · Esc to exit'
          : 'Connect once — click two nodes · click Connect again to keep chaining · Esc to exit',
        color: '#38bdf8',
      }
    : stampMeta
      ? { text: `${stampMeta.label} — click to drop, or drag to size · ${mode} · Esc`, color: stampMeta.color }
      : tool === 'none'
        ? null
        : { text: `${HINTS[tool].text} · ${mode} · Esc`, color: HINTS[tool].color };

  if (!hint) return null;

  return (
    <div className="absolute bottom-20 left-1/2 z-50" style={{ transform: 'translateX(-50%)' }}>
      <div
        className="backdrop-blur-md border rounded-xl px-5 py-2.5 shadow-xl flex items-center gap-3"
        style={{ backgroundColor: `${hint.color}22`, borderColor: hint.color }}
      >
        <span className="text-xs font-semibold" style={{ color: hint.color }}>
          {hint.text}
        </span>
        <button
          onClick={onExit}
          className="text-xs opacity-60 hover:opacity-100"
          style={{ color: hint.color }}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
